import { distinctUntilChanged, map } from 'rxjs/operators';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CertemyValidators } from 'validators/validators';
import { DynamicFormField } from 'interfaces/dynamic-form-field.interface';
import { constants, LOG_HALF_UNIT_PATTERN } from 'constants/constants';
import { ResizeService } from 'services/resize/resize.service';
import { email, patternValidator, required } from '@certemy/common';
import { MASK_TYPE, NumberMaskConfig } from '@certemy/ui-components';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'cm-log-corrections-form',
  templateUrl: './corrections-form.component.html',
  styleUrls: ['./corrections-form.component.scss'],
})
export class LogCorrectionsFormComponent implements OnInit, OnDestroy {
  @Input() showErrors = false;
  @Input() entries;
  @Output() register = new EventEmitter();

  elements: string[] = [];
  dynamicForm: UntypedFormArray;
  FIELD_TYPES = constants.FORM_FIELD_TYPE_IDS;
  isMobile = false;
  numberMaskConfig: NumberMaskConfig = {
    maskType: MASK_TYPE.MULTIPLE_OF_HALF,
  };

  constructor(
    private fb: UntypedFormBuilder,
    private resizeService: ResizeService,
    private _changeDetectionRef: ChangeDetectorRef,
  ) {
    this.resizeService.resize$.pipe(untilDestroyed(this)).subscribe(({ isMobile }) => (this.isMobile = isMobile));
  }

  ngOnInit() {
    this.register.emit(this);
    this.dynamicForm = this.fb.array(this.createForm());
    this.dynamicForm.controls.forEach((control: UntypedFormGroup) => {
      this.subscribeOnRowChange(control);
      this._changeDetectionRef.detectChanges();
    });
    this.elements = this.getFormElems();
  }

  ngOnDestroy() {}

  private createForm(entries = this.entries) {
    return entries
      .map((entry) => {
        return entry.fields;
      })
      .map((entry: DynamicFormField[]) => {
        return this.fb.group({
          selected: true,
          elements: this.createEntryControls(entry),
        });
      });
  }

  private createEntryControls(elements) {
    const entryElems = elements.reduce((entryControls, element) => {
      let value;
      if (element.type.id === this.FIELD_TYPES.RADIO) {
        value = element.value || element.value === 0 ? element.value : null;
      } else if (element.type.id === this.FIELD_TYPES.SELECT) {
        value = element.value || element.value === 0 ? element.value : '';
      } else {
        value = element.value || '';
      }
      const validators = [];

      if (element.required) {
        validators.push(required);
      }

      if (element.type.id === this.FIELD_TYPES.EMAIL) {
        validators.push(email);
      }

      if (element.type.id === this.FIELD_TYPES.NUMBER && element.label === 'Hours') {
        validators.push(patternValidator(LOG_HALF_UNIT_PATTERN));
        validators.push(CertemyValidators.emptyNumberFieldValidator());
      }

      entryControls[element.id] = [value, Validators.compose(validators)];
      return entryControls;
    }, {});

    return this.fb.group(entryElems);
  }

  private getFormElems() {
    if (!this.entries || !this.entries.length) {
      return [];
    }

    return this.entries[0].fields;
  }

  getCorrectionsFormData() {
    if (this.isFormInvalid()) {
      return false;
    }

    return this.dynamicForm.value
      .filter((entryGroup) => entryGroup.selected)
      .map((entryGroup) => this.mapEntryGroup(entryGroup));
  }

  private mapEntryGroup(entryGroup) {
    const entryFields = entryGroup.elements;
    const fields = this.elements.map((elem) => this.mapEntryField(elem, entryFields));
    return { fields };
  }

  private mapEntryField(element, entryFields) {
    let value = entryFields[element.id];
    if (element.type.id === constants.STEP_CONTENT_ELEMENTS_TYPES.SELECT) {
      value = value !== '' ? Number(value) : '';
    }

    return {
      id: element.id,
      value,
    };
  }

  private subscribeOnRowChange(control: UntypedFormGroup) {
    control.valueChanges
      .pipe(
        untilDestroyed(this),
        map((value) => value.selected),
        distinctUntilChanged(),
      )
      .subscribe((isEntrySelected) => {
        const elementsGroup = control.get('elements');
        if (isEntrySelected) {
          elementsGroup.enable();
        } else {
          elementsGroup.disable();
        }
      });
  }

  private isFormInvalid(): boolean {
    return this.dynamicForm.controls
      .filter((control) => control.get('selected').value)
      .some((control) => control.invalid);
  }
}
